import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Loader2, Package, Mail, Settings2 } from "lucide-react";
import { http, errText, money } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

const Stat = ({ label, value, testId }) => (
  <div className="bg-white border-2 border-[#0B0B0B] p-5">
    <p className="font-mono-x uppercase text-[11px] tracking-[0.2em] text-zinc-600">{label}</p>
    <p className="font-display text-4xl mt-2" data-testid={testId}>{value}</p>
  </div>
);

const addr = (a) => (a ? [a.name, a.line1, a.line2, `${a.city ?? ""} ${a.state ?? ""} ${a.postal_code ?? ""}`.trim(), a.country].filter(Boolean).join(", ") : "—");

const Orders = ({ orders, onToggle }) => (
  <div className="overflow-x-auto bg-white border-2 border-[#0B0B0B]" data-lenis-prevent>
    <table className="w-full text-sm min-w-[760px]" data-testid="orders-table">
      <thead className="bg-[#0B0B0B] text-white font-mono-x uppercase text-xs tracking-wider">
        <tr>{["Date", "Customer", "Ship to", "Qty", "Total", "Shipped"].map((h) => <th key={h} className="text-left px-4 py-3">{h}</th>)}</tr>
      </thead>
      <tbody>
        {orders.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center font-semibold text-zinc-600" data-testid="orders-empty">No paid orders yet.</td></tr>}
        {orders.map((o) => (
          <tr key={o.id} className="border-t border-zinc-200 align-top" data-testid={`order-row-${o.id}`}>
            <td className="px-4 py-3 whitespace-nowrap">{new Date(o.created_at).toLocaleDateString()}</td>
            <td className="px-4 py-3"><p className="font-bold">{o.customer_name || "—"}</p><p className="text-zinc-600">{o.customer_email}</p></td>
            <td className="px-4 py-3 max-w-[260px]">{addr(o.shipping_address)}</td>
            <td className="px-4 py-3 font-bold">{o.quantity}</td>
            <td className="px-4 py-3 font-bold">{money(o.amount)}</td>
            <td className="px-4 py-3">
              <button
                onClick={() => onToggle(o)}
                data-testid={`order-fulfill-toggle-${o.id}`}
                className={`px-3 py-1.5 font-mono-x text-xs uppercase tracking-wider border-2 transition-colors duration-200 ${o.fulfilled ? "bg-[#E10600] text-white border-[#E10600]" : "border-[#0B0B0B] hover:bg-[#0B0B0B] hover:text-white"}`}
              >
                {o.fulfilled ? "Shipped" : "Mark shipped"}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Waitlist = ({ list }) => {
  const copy = () => { navigator.clipboard?.writeText(list.map((w) => w.email).join(", ")); toast.success("Emails copied"); };
  return (
    <div className="bg-white border-2 border-[#0B0B0B] p-5" data-testid="waitlist-panel">
      <div className="flex justify-between items-center gap-4 mb-4">
        <p className="font-bold">{list.length} fans waiting for the next drop</p>
        <button onClick={copy} disabled={!list.length} data-testid="waitlist-copy-button" className="bg-[#0B0B0B] text-white px-4 py-2 font-mono-x text-xs uppercase tracking-wider transition-colors duration-200 hover:bg-[#E10600] disabled:opacity-50">Copy all emails</button>
      </div>
      <ul className="divide-y divide-zinc-200">
        {list.map((w) => <li key={w.id} className="py-2 flex justify-between gap-4 text-sm"><span className="font-semibold break-all">{w.email}</span><span className="text-zinc-600 whitespace-nowrap">{new Date(w.created_at).toLocaleDateString()}</span></li>)}
      </ul>
    </div>
  );
};

const ProductEditor = ({ product, onSaved }) => {
  const [form, setForm] = useState({ price: product.price, shipping: product.shipping, stock: product.stock, description: product.description });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await http.put("/admin/product", { price: Number(form.price), shipping: Number(form.shipping), stock: parseInt(form.stock, 10), description: form.description });
      onSaved(data);
      toast.success("Hat updated");
    } catch (err) {
      toast.error(errText(err));
    }
    setSaving(false);
  };
  const field = "w-full border-2 border-[#0B0B0B] px-4 py-3 font-semibold outline-none focus:border-[#E10600] transition-colors duration-200";
  return (
    <form onSubmit={save} className="bg-white border-2 border-[#0B0B0B] p-6 grid sm:grid-cols-3 gap-5" data-testid="product-editor">
      {[["price", "Hat price ($)", "0.01"], ["shipping", "Shipping ($)", "0.01"], ["stock", "Hats in stock", "1"]].map(([k, l, step]) => (
        <label key={k} className="block">
          <span className="font-mono-x uppercase text-xs tracking-[0.2em]">{l}</span>
          <input type="number" min="0" step={step} required value={form[k]} onChange={set(k)} className={`${field} mt-2`} data-testid={`product-${k}-input`} />
        </label>
      ))}
      <label className="block sm:col-span-3">
        <span className="font-mono-x uppercase text-xs tracking-[0.2em]">Description</span>
        <textarea rows={4} required value={form.description} onChange={set("description")} className={`${field} mt-2`} data-testid="product-description-input" />
      </label>
      <button type="submit" disabled={saving} data-testid="product-save-button" className="sm:col-span-3 bg-[#E10600] text-white py-4 font-display uppercase text-xl flex items-center justify-center gap-2 transition-colors duration-200 hover:bg-[#0B0B0B] disabled:opacity-60">
        {saving && <Loader2 className="w-5 h-5 animate-spin" />} Save changes
      </button>
    </form>
  );
};

export default function Admin() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      await http.get("/auth/me");
      const [o, w, p] = await Promise.all([http.get("/admin/orders"), http.get("/admin/waitlist"), http.get("/product")]);
      setData({ orders: Array.isArray(o.data) ? o.data : [], waitlist: Array.isArray(w.data) ? w.data : [], product: p.data });
    } catch {
      localStorage.removeItem("xsquad_admin_token");
      nav("/admin/login", { replace: true });
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (o) => {
    try {
      const { data: upd } = await http.patch(`/admin/orders/${o.id}`, { fulfilled: !o.fulfilled });
      setData((d) => ({ ...d, orders: d.orders.map((x) => (x.id === o.id ? upd : x)) }));
    } catch (err) { toast.error(errText(err)); }
  };

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    localStorage.removeItem("xsquad_admin_token");
    nav("/admin/login");
  };

  if (!data) return <main className="min-h-[100svh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#E10600]" /></main>;

  const revenue = data.orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.amount ?? 0), 0);
  const hats = data.orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.quantity ?? 0), 0);

  return (
    <main data-testid="admin-dashboard" className="min-h-[100svh] bg-[#F6F3EF] px-5 sm:px-10 lg:px-16 pt-28 pb-20">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="font-display uppercase text-5xl sm:text-6xl">Owner <span className="text-[#E10600]">HQ</span></h1>
        <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-2 border-2 border-[#0B0B0B] px-4 py-2 font-mono-x text-xs uppercase tracking-wider transition-colors duration-200 hover:bg-[#0B0B0B] hover:text-white"><LogOut className="w-4 h-4" /> Log out</button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
        <Stat label="Revenue" value={money(revenue)} testId="stat-revenue" />
        <Stat label="Hats sold" value={hats} testId="stat-hats-sold" />
        <Stat label="In stock" value={data.product?.stock ?? 0} testId="stat-stock" />
        <Stat label="Waitlist" value={data.waitlist.length} testId="stat-waitlist" />
      </div>
      <Tabs defaultValue="orders" className="mt-10">
        <TabsList className="bg-transparent p-0 gap-2 h-auto flex-wrap justify-start">
          {[["orders", Package, "Orders"], ["waitlist", Mail, "Waitlist"], ["product", Settings2, "Edit hat"]].map(([v, Icon, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="rounded-none border-2 border-[#0B0B0B] px-4 py-2 font-display uppercase text-lg data-[state=active]:bg-[#E10600] data-[state=active]:text-white data-[state=active]:border-[#E10600]">
              <Icon className="w-4 h-4 mr-2" /> {l}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="orders" className="mt-6"><Orders orders={data.orders} onToggle={toggle} /></TabsContent>
        <TabsContent value="waitlist" className="mt-6"><Waitlist list={data.waitlist} /></TabsContent>
        <TabsContent value="product" className="mt-6"><ProductEditor product={data.product} onSaved={(p) => setData((d) => ({ ...d, product: p }))} /></TabsContent>
      </Tabs>
    </main>
  );
}
